import {
  AnswerSemanticEntity,
  AxiosAPIError,
  ChoiceEntity,
  ChoiceListEntity,
  CreatedChoiceEntity,
  ChoicesQuery,
  FanAnswers,
  FansAnswersQuery,
  ListEntity,
  ListEntityV2,
  PaginationQuery,
  Question,
  QuestionChoicesDataValidator,
  QuestionFormDataType,
  SurveyAdvancedQuery,
  SurveyAnswersResponse,
  SurveyAnswersValidator,
  SurveyEntity,
  SurveyExtensionPoint,
  SurveyInstanceEntity,
  SurveyInstanceQuery,
  SurveyInstanceValidator,
  SurveyPaginationQuery,
  SurveyQuery,
  SurveyValidator,
  SurveyUpdateValidator,
  UserRole,
  questionChoicesKeys,
  questionsKeys,
  surveyFansAnswers,
  surveyInstancesKeys,
  surveysKeys,
} from '@seaters/constants';
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  createAdminSurvey,
  createAdminSurveyInstance,
  deleteAdminSurvey,
  deleteAdminSurveyInstance,
  deleteAdminQuestion,
  fetchAdminSurveys,
  fetchAdminSurveysInstances,
  fetchQuestionChoices,
  fetchSurveyAnswers,
  fetchAdminSurveyById,
  fetchAdminSurveyInstanceById,
  fetchSurveyInstancesForFanGroup,
  fetchSurveyInstancesForWishList,
  fetchAdminSurveysQuestions,
  sendAnswers,
  updateAdminSurvey,
  updateAdminSurveyInstance,
  createAdminQuestion,
  updateAdminQuestion,
  createAdminQuestionChoices,
  updateAdminQuestionChoices,
  fetchAdminAnswerSemantics,
  fetchFGOWishListSurveysInstances,
  fetchSurveyFansAnswers,
  createFGOSurveyInstance,
  updateFGOnSurveyInstance,
  fetchAdvancedSurveys,
  fetchFanGroupOwnerSurveys,
  sendFanAnswersAsFGO,
  sendGuestAnswersAsHost,
  fetchSurveyInstancesWithoutHeaders,
  fetchQuestionChoicesWithoutHeaders,
  sendAnswersWithoutHeaders,
  fetchSurveyAnswersWithoutHeaders,
} from '../api';
import {
  isVersionConflict,
  invalidateVersionedQueries,
  useVersionedSnapshot,
  withExpectedVersion,
} from '../utils/versionedUpdate';

export const useFetchAdminSurveys = (
  isAdmin: boolean,
  params: SurveyPaginationQuery
) =>
  useQuery<ListEntity<SurveyEntity>, Error>({
    queryKey: surveysKeys.list(params),
    queryFn: () => fetchAdminSurveys(params),
    enabled: isAdmin,
  });

export const useFetchAdvancedSurveys = (
  userRole: UserRole.FAN_GROUP_OWNER | UserRole.ADMIN,
  params: SurveyAdvancedQuery
) =>
  useQuery<ListEntity<SurveyEntity>, Error>({
    queryKey: surveysKeys.list(params),
    queryFn: () => fetchAdvancedSurveys(userRole, params),
  });

export const useFetchFanGroupOwnerSurveys = (
  enabled: boolean,
  params: SurveyPaginationQuery
) =>
  useQuery<ListEntity<SurveyEntity>, Error>({
    queryKey: surveysKeys.list(params),
    queryFn: () => fetchFanGroupOwnerSurveys(params),
    enabled,
  });

export const useFetchAdminAnswerSemantics = (params: SurveyPaginationQuery) =>
  useQuery<ListEntity<AnswerSemanticEntity>, Error>({
    queryKey: questionsKeys.list(params),
    queryFn: () => fetchAdminAnswerSemantics(params),
  });

export const useFetchAdminSurveysInstances = (
  isAdmin: boolean,
  params: SurveyInstanceQuery
) =>
  useQuery<ListEntity<SurveyInstanceEntity>, Error>({
    queryKey: surveyInstancesKeys.list(params),
    queryFn: () => fetchAdminSurveysInstances(params),
    enabled: isAdmin,
  });

export const useFetchAdminSurvey = (surveyId: string) =>
  useQuery<SurveyEntity, Error>({
    queryKey: surveysKeys.detail(surveyId),
    queryFn: () => fetchAdminSurveyById(surveyId),
    enabled: !!surveyId,
  });

export const useFetchAdminSurveyInstance = (surveyInstanceId: string) =>
  useQuery<SurveyInstanceEntity, Error>({
    queryKey: surveyInstancesKeys.detail(surveyInstanceId),
    queryFn: () => fetchAdminSurveyInstanceById(surveyInstanceId),
    enabled: !!surveyInstanceId,
  });

export const useFetchAdminSurveysQuestions = (
  params: PaginationQuery,
  enabled = true
) =>
  useQuery<ListEntity<Question>, Error>({
    queryKey: questionsKeys.list(params),
    queryFn: () => fetchAdminSurveysQuestions(params),
    refetchOnMount: false,
    enabled,
  });

export const useFetchFGOWishListSurveysInstances = (
  waitinglistId: string,
  params: SurveyQuery
) =>
  useQuery<ListEntityV2<SurveyInstanceEntity>, Error>({
    queryKey: surveyInstancesKeys.listPerWL(params),
    queryFn: () => fetchFGOWishListSurveysInstances(waitinglistId, params),
  });

export const useCreateAdminSurvey = (): UseMutationResult<
  SurveyEntity,
  AxiosAPIError<SurveyValidator>,
  SurveyValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: surveysKeys.mutation(),
    mutationFn: (body: SurveyValidator) => createAdminSurvey(body),
    onSuccess: () => {
      // Invalidate all survey lists so new surveys appear
      queryClient.invalidateQueries({ queryKey: surveysKeys.all() });
    },
  });
};

export const useUpdateAdminSurvey = (
  id: string,
  snapshotVersion?: number
): UseMutationResult<
  SurveyEntity,
  AxiosAPIError<SurveyUpdateValidator>,
  SurveyUpdateValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  const cachedVersion = queryClient.getQueryData<SurveyEntity>(
    surveysKeys.detail(id)
  )?.version;
  const { acceptServerVersion, getExpectedVersion } = useVersionedSnapshot(
    id,
    snapshotVersion ?? cachedVersion
  );

  return useMutation({
    mutationKey: surveysKeys.mutation(),
    mutationFn: (body: SurveyUpdateValidator) =>
      updateAdminSurvey(id, withExpectedVersion(body, getExpectedVersion())),
    onSuccess: (updatedSurvey) => {
      acceptServerVersion(updatedSurvey);
      queryClient.setQueryData(surveysKeys.detail(id), updatedSurvey);
    },
    onError: async (error) => {
      if (isVersionConflict(error)) {
        await invalidateVersionedQueries(queryClient, surveysKeys.detail(id));
      }
    },
  });
};

export const useDeleteSurvey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      surveyId,
      companyId,
    }: {
      surveyId: string;
      companyId?: string;
    }) => deleteAdminSurvey(surveyId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveysKeys.all() });
    },
  });
};

export const useCreateAdminSurveyInstance = (): UseMutationResult<
  SurveyInstanceEntity,
  AxiosAPIError<SurveyInstanceValidator>,
  SurveyInstanceValidator,
  unknown
> =>
  useMutation({
    mutationKey: surveyInstancesKeys.mutation(),
    mutationFn: (body: SurveyInstanceValidator) =>
      createAdminSurveyInstance(body),
  });

export const useCreateFGOSurveyInstance = (): UseMutationResult<
  SurveyInstanceEntity,
  AxiosAPIError<SurveyInstanceValidator>,
  SurveyInstanceValidator,
  unknown
> =>
  useMutation({
    mutationKey: surveyInstancesKeys.mutation(),
    mutationFn: (body: SurveyInstanceValidator) =>
      createFGOSurveyInstance(body),
  });

export const useUpdateAdminSurveyInstance = (
  id: string
): UseMutationResult<
  SurveyInstanceEntity,
  AxiosAPIError<SurveyInstanceValidator>,
  SurveyInstanceValidator,
  unknown
> =>
  useMutation({
    mutationKey: surveyInstancesKeys.mutation(),
    mutationFn: (body: SurveyInstanceValidator) =>
      updateAdminSurveyInstance(id, body),
  });

export const useUpdateFGOSurveyInstance = (): UseMutationResult<
  SurveyInstanceEntity,
  AxiosAPIError<{ id: string; body: SurveyInstanceValidator }>,
  { id: string; body: SurveyInstanceValidator },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: surveyInstancesKeys.mutation(),
    mutationFn: ({ id, body }: { id: string; body: SurveyInstanceValidator }) =>
      updateFGOnSurveyInstance(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveyInstancesKeys.all() });
    },
  });
};

export const useDeleteAdminSurveyInstance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ surveyInstanceId }: { surveyInstanceId: string }) =>
      deleteAdminSurveyInstance(surveyInstanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveyInstancesKeys.all() });
    },
  });
};

export const useFetchWaitingListSurveyInstances = (
  params: Omit<SurveyQuery, 'fangroup_id'>
) =>
  useQuery<ListEntityV2<SurveyInstanceEntity>, Error>({
    queryKey: surveyInstancesKeys.listPerWL(params),
    queryFn: () => fetchSurveyInstancesForWishList(params),
    // keepPreviousData: true,
    // refetchOnMount: false,
    // refetchOnReconnect: false,
    // refetchOnWindowFocus: false,
    enabled: !!params.waitinglist_id,
  });

export const useFetchWaitingListSurveyInstancesWithoutHeader = (
  params: Omit<SurveyQuery, 'fangroup_id'>
) =>
  useQuery<ListEntityV2<SurveyInstanceEntity>, Error>({
    queryKey: surveyInstancesKeys.listPerWL(params),
    queryFn: () => fetchSurveyInstancesWithoutHeaders(params),
    enabled: !!params.waitinglist_id,
    retry: 3,
  });

export const useFetchAfterRegistrationSurveyInstances = (
  params: Omit<SurveyQuery, 'waitinglist_id'>,
  slug: string,
  enabled: boolean
) =>
  useQuery<ListEntityV2<SurveyInstanceEntity>, Error>({
    queryKey: surveyInstancesKeys.listPerFan(
      params?.fangroup_id ?? '',
      params?.extension_point
    ),
    queryFn: () => fetchSurveyInstancesForFanGroup(params),
    enabled: enabled && (!!params.fangroup_id || !!slug),
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });

export const useFetchWaitingListSurveyAnswers = (
  waitingListId: string,
  instanceId: string
) =>
  useQuery<ListEntityV2<SurveyAnswersResponse>, Error>({
    queryKey: surveyInstancesKeys.answersBeforeJoin(
      {
        waitinglist_id: waitingListId,
        extension_point: SurveyExtensionPoint.BEFORE_JOINING_WAITINGLIST,
      },
      instanceId
    ),
    queryFn: () => fetchSurveyAnswers(instanceId),
    enabled: !!instanceId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });

export const useFetchWaitingListSurveyAnswersAtCheckout = (
  waitingListId: string,
  instanceId: string
) =>
  useQuery<ListEntityV2<SurveyAnswersResponse>, Error>({
    queryKey: surveyInstancesKeys.answersAtCheckout(
      {
        waitinglist_id: waitingListId,
        extension_point: SurveyExtensionPoint.AT_CHECKOUT,
      },
      instanceId
    ),
    queryFn: () => fetchSurveyAnswers(instanceId),
    enabled: !!instanceId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });

export const useFetchWaitingListSurveyAnswersWithoutHeaders = (
  waitingListId: string,
  instanceId: string,
  code: string,
  contextWaitingListId?: string | null
) =>
  useQuery<ListEntityV2<SurveyAnswersResponse>, Error>({
    queryKey: surveyInstancesKeys.answersAfterEvent(
      {
        waitinglist_id: waitingListId,
        extension_point: SurveyExtensionPoint.AFTER_EVENT,
      },
      instanceId
    ),
    queryFn: () =>
      fetchSurveyAnswersWithoutHeaders(instanceId, code, contextWaitingListId),
    enabled: !!instanceId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    retry: 3,
  });

export const useFetchFanSurveyAnswers = (
  fanGroupId: string,
  instanceId: string
) =>
  useQuery<ListEntityV2<SurveyAnswersResponse>, Error>({
    queryKey: surveyInstancesKeys.answersAfterSignIn(fanGroupId, instanceId),
    queryFn: () => fetchSurveyAnswers(instanceId),
    enabled: !!instanceId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });

export const useFetchQuestionChoices = (
  questionId: string,
  params: ChoicesQuery
) => {
  return useQuery<ListEntityV2<ChoiceEntity>, Error>({
    queryKey: questionChoicesKeys.listPerQuestion(questionId),
    queryFn: () => fetchQuestionChoices(questionId, params),
    enabled: !!questionId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });
};

export const useFetchQuestionChoicesWithoutHeaders = (
  questionId: string,
  params: ChoicesQuery
) => {
  return useQuery<ListEntityV2<ChoiceEntity>, Error>({
    queryKey: questionChoicesKeys.listPerQuestion(questionId),
    queryFn: () => fetchQuestionChoicesWithoutHeaders(questionId, params),
    enabled: !!questionId,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
  });
};

export const useSendAnswers = (
  instanceId: string,
  waitingListId?: string,
  contextWaitingListId?: string | null
): UseMutationResult<
  SurveyAnswersResponse,
  AxiosAPIError<SurveyAnswersValidator>,
  SurveyAnswersValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return waitingListId
    ? useMutation({
        mutationFn: (body: SurveyAnswersValidator) =>
          sendAnswers(instanceId, body, contextWaitingListId),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: surveyInstancesKeys.answersBeforeJoin(
              {
                waitinglist_id: waitingListId,
                extension_point:
                  SurveyExtensionPoint.BEFORE_JOINING_WAITINGLIST,
              },
              instanceId
            ),
          });
          queryClient.invalidateQueries({
            queryKey: surveyInstancesKeys.answersAtCheckout(
              {
                waitinglist_id: waitingListId,
                extension_point: SurveyExtensionPoint.AT_CHECKOUT,
              },
              instanceId
            ),
          });
        },
      })
    : useMutation({
        mutationKey: surveyInstancesKeys.mutation(),
        mutationFn: (body: SurveyAnswersValidator) =>
          sendAnswers(instanceId, body, contextWaitingListId),
      });
};

export const useSendAnswersAfterEvent = (
  instanceId: string,
  code: string,
  contextWaitingListId?: string | null
): UseMutationResult<
  SurveyAnswersResponse,
  AxiosAPIError<SurveyAnswersValidator>,
  SurveyAnswersValidator,
  unknown
> => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SurveyAnswersValidator) =>
      sendAnswersWithoutHeaders(instanceId, code, body, contextWaitingListId),
    onSuccess: () => {
      // queryClient.invalidateQueries(
      //   surveyInstancesKeys.answersBeforeJoin(
      //     {
      //       waitinglist_id: waitingListId,
      //       extension_point: SurveyExtensionPoint.BEFORE_JOINING_WAITINGLIST,
      //     },
      //     instanceId
      //   )
      // );
    },
  });
};

export const useSendFanAnswersAsFGO = (
  instanceId: string,
  fanId: string,
  waitingListId?: string
): UseMutationResult<
  SurveyAnswersResponse,
  AxiosAPIError<SurveyAnswersValidator>,
  SurveyAnswersValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SurveyAnswersValidator) =>
      sendFanAnswersAsFGO(instanceId, fanId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveyInstancesKeys.answersAtCheckout(
          {
            waitinglist_id: waitingListId,
            extension_point: SurveyExtensionPoint.AT_CHECKOUT,
          },
          instanceId
        ),
      });
    },
  });
};

export const useSendGuestAnswersAsHost = (
  instanceId: string,
  fanId: string,
  waitingListId?: string
): UseMutationResult<
  SurveyAnswersResponse,
  AxiosAPIError<SurveyAnswersValidator>,
  SurveyAnswersValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SurveyAnswersValidator) =>
      sendGuestAnswersAsHost(instanceId, fanId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: surveyInstancesKeys.answersAtCheckout(
          {
            waitinglist_id: waitingListId,
            extension_point: SurveyExtensionPoint.AT_CHECKOUT,
          },
          instanceId
        ),
      });
    },
  });
};

export const useCreateAdminQuestion = (): UseMutationResult<
  Question,
  AxiosAPIError<Question>,
  QuestionFormDataType,
  unknown
> =>
  useMutation({
    mutationKey: questionsKeys.all(),
    mutationFn: (body: QuestionFormDataType) => createAdminQuestion(body),
  });

export const useUpdateAdminQuestion = (
  id: string,
  surveyId: string
): UseMutationResult<
  Question,
  AxiosAPIError<Question>,
  QuestionFormDataType,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: questionsKeys.mutation(),
    mutationFn: (body: QuestionFormDataType) => updateAdminQuestion(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surveysKeys.detail(surveyId) });
    },
  });
};

export const useDeleteAdminQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => deleteAdminQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questionsKeys.all() });
    },
  });
};

export const useCreateAdminQuestionChoices = (): UseMutationResult<
  CreatedChoiceEntity[],
  AxiosAPIError<QuestionChoicesDataValidator>,
  { questionId: string; body: QuestionChoicesDataValidator },
  unknown
> =>
  useMutation({
    mutationKey: questionChoicesKeys.listPerQuestion(''),
    mutationFn: ({
      questionId,
      body,
    }: {
      questionId: string;
      body: QuestionChoicesDataValidator;
    }) => createAdminQuestionChoices(questionId, body),
  });

export const useUpdateAdminQuestionChoices = (
  questionId: string
): UseMutationResult<
  ChoiceListEntity[],
  AxiosAPIError<QuestionChoicesDataValidator>,
  QuestionChoicesDataValidator,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: questionsKeys.mutation(),
    mutationFn: (body: QuestionChoicesDataValidator) =>
      updateAdminQuestionChoices(questionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: questionChoicesKeys.listPerQuestion(questionId),
      });
    },
  });
};

// fetchSurveyFansAnswers

export const useFetchSurveysFansAnswers = (
  waitinglistId: string,
  surveyInstanceId: string,
  params: FansAnswersQuery
) => {
  return useQuery<ListEntityV2<FanAnswers>, Error>({
    queryKey: surveyFansAnswers.listPerInstance(
      waitinglistId,
      surveyInstanceId,
      params
    ),
    queryFn: () =>
      fetchSurveyFansAnswers(waitinglistId, surveyInstanceId, params),
    enabled: !!surveyInstanceId && !!params?.user_id,
  });
};
